import type {ReactElement} from 'react';
import {useCallback, useEffect, useRef} from 'react';
import styles from './audio-waveform-visualizer.module.scss';
import type {AudioWaveformVisualizerProps, StreamBar} from './audio-waveform-visualizer.types';
import {BAR_COLOR_ACTIVE, BAR_COLOR_INACTIVE, MAX_HEIGHT_RATIO, NOISE_GATE, DEFAULT_FRAMES_PER_BAR} from './audio-waveform-visualizer.types';
import {drawBar} from './audio-waveform-visualizer.util';

export function AudioWaveformVisualizer({
  analyserNode,
  isRecording,
  isFrozen,
  barWidth = 4,
  gap = 1,
  framesPerBar = DEFAULT_FRAMES_PER_BAR,
}: AudioWaveformVisualizerProps): ReactElement {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<StreamBar[]>([]);
  const scrollOffsetRef = useRef<number>(0);
  const rafRef = useRef<number | null>(null);

  const getDpr = (): number => window.devicePixelRatio || 1;

  const getBarMetrics = useCallback(
    (canvasW: number, canvasH: number) => {
      const dpr = getDpr();
      const barW = Math.round(barWidth * dpr);
      const gapW = Math.round(barWidth * gap * dpr);
      const unit = barW + gapW;
      const maxH = Math.round(canvasH * MAX_HEIGHT_RATIO);
      const streamCapacity = Math.ceil(canvasW / unit) + 2;
      return {barW, unit, maxH, streamCapacity};
    },
    [barWidth, gap]
  );

  const getAmplitude = useCallback((analyser: AnalyserNode): number => {
    const data = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(data);
    const end = Math.min(17, data.length - 1);
    let sum = 0;
    for (let i = 1; i <= end; i++) {
      sum += data[i];
    }
    const raw = sum / (end * 255);

    if (raw < NOISE_GATE) {
      return 0;
    }

    return Math.pow((raw - NOISE_GATE) / (1 - NOISE_GATE), 0.5);
  }, []);

  const syncCanvasSize = useCallback((canvas: HTMLCanvasElement): {canvasW: number; canvasH: number} | null => {
    const cssW = canvas.clientWidth;
    const cssH = canvas.clientHeight;

    if (cssW === 0 || cssH === 0) {
      return null;
    }

    const dpr = getDpr();
    const canvasW = Math.round(cssW * dpr);
    const canvasH = Math.round(cssH * dpr);
    if (canvas.width !== canvasW || canvas.height !== canvasH) {
      canvas.width = canvasW;
      canvas.height = canvasH;
    }
    return {canvasW, canvasH};
  }, []);

  const drawStream = useCallback(
    (ctx: CanvasRenderingContext2D, canvasW: number, canvasH: number, stream: StreamBar[], scrollOffset: number, frozen: boolean): void => {
      const {barW, unit, maxH} = getBarMetrics(canvasW, canvasH);
      const centerY = canvasH / 2;

      ctx.clearRect(0, 0, canvasW, canvasH);

      for (let i = 0; i < stream.length; i++) {
        const posFromNewest = stream.length - 1 - i;
        const x = canvasW - barW - posFromNewest * unit - scrollOffset;

        if (x + barW < 0) {
          continue;
        }

        if (x > canvasW) {
          continue;
        }

        const {amplitude, recorded} = stream[i];
        const h = recorded ? barW + Math.round(amplitude * (maxH - barW)) : barW;
        const y = Math.round(centerY - h / 2);

        ctx.fillStyle = frozen || !recorded ? BAR_COLOR_INACTIVE : BAR_COLOR_ACTIVE;
        drawBar(ctx, x, y, barW, h, barW / 2);
      }
    },
    [getBarMetrics]
  );

  useEffect(() => {
    if (!isRecording || !analyserNode) {
      return;
    }

    const canvas = canvasRef.current;
    const initialSize = canvas ? getBarMetrics(Math.round(canvas.clientWidth * getDpr()), Math.round(canvas.clientHeight * getDpr())).streamCapacity : 60;
    streamRef.current = Array.from({length: initialSize}, () => ({amplitude: 0, recorded: false}));
    scrollOffsetRef.current = 0;

    const tick = (): void => {
      const c = canvasRef.current;
      if (!c) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const ctx = c.getContext('2d');
      if (!ctx) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const size = syncCanvasSize(c);
      if (!size) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const {canvasW, canvasH} = size;
      const {unit, streamCapacity} = getBarMetrics(canvasW, canvasH);

      scrollOffsetRef.current += unit / framesPerBar;

      if (scrollOffsetRef.current >= unit) {
        scrollOffsetRef.current -= unit;
        const amplitude = getAmplitude(analyserNode);
        streamRef.current.shift();
        streamRef.current.push({amplitude, recorded: true});
        if (streamRef.current.length > streamCapacity) {
          streamRef.current.shift();
        }
      }

      drawStream(ctx, canvasW, canvasH, streamRef.current, scrollOffsetRef.current, false);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return (): void => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isRecording, analyserNode, syncCanvasSize, drawStream, getBarMetrics, getAmplitude, framesPerBar]);

  useEffect(() => {
    if (!isFrozen) {
      return;
    }

    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    const canvas = canvasRef.current;

    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return;
    }

    const size = syncCanvasSize(canvas);

    if (!size) {
      return;
    }

    drawStream(ctx, size.canvasW, size.canvasH, streamRef.current, scrollOffsetRef.current, true);
  }, [isFrozen, syncCanvasSize, drawStream]);

  useEffect(() => {
    if (isRecording || isFrozen) {
      return;
    }

    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    streamRef.current = [];
    scrollOffsetRef.current = 0;
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, [isRecording, isFrozen]);

  return <canvas ref={canvasRef} className={styles.canvas} />;
}
